import { useState, useEffect, useContext, useRef } from "react";

import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate, useParams } from "react-router";
import IBadgeFormat from "../../../dataProvider/interfaces/IBadgeFormat";
import { APIContext } from "../../../store/APIStore";
import { Loading, useRedirect } from "react-admin";
import CloseIcon from "@mui/icons-material/Close";
import { routePaths } from "../../../baseLayout/SidebarMenu";
import NotificationMessage, {
  notificationType,
} from "../../../components/NotificationMessage/NotificationMessage";
import { BiTrash } from "react-icons/bi";
import { IoMdCopy } from "react-icons/io";
import {
  actionDialogType,
  RouteLeavingGuard,
  SimpleActionDialog,
} from "../../../components";
import FdButton from "../../../components/UIComponents/Button/FdButton";
import FdIconButton from "../../../components/UIComponents/IconButton/FdIconButton";
import { badgeSizeForm as classes } from "../styles";
import FdChip from "../../../components/UIComponents/Chip/FdChip";
import FdTypography from "../../../components/UIComponents/Typography/FdTypography";
import FdTextField from "../../../components/UIComponents/TextField/FdTextfield";
import FdCheckbox from "../../../components/UIComponents/Checkbox/FdCheckbox";
import { useForm, Controller } from "react-hook-form";

const BadgeSizeForm = () => {
  const [badgeFormat, setBadgeFormat] = useState<IBadgeFormat>();
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    title: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onConfirm: () => {},
  });
  const [dirtyForm, setDirtyForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const { formatId } = useParams() as any;
  const api = useContext(APIContext);
  let navigate = useNavigate();
  const redirect = useRedirect();
  // let submit: Function;
  const notificationRef = useRef<any>(); // notification bar

  useEffect(() => {
    if (formatId) {
      fetchBadgeFormat();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBadgeFormat = async () => {
    try {
      setLoading(true);
      const data = await api.badgeFormatService.getBadgeFormat(formatId);
      setBadgeFormat(data);
      setLoading(false);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const getBadgeUseCases = () => {
    if (badgeFormat && badgeFormat.appliesTo) {
      return badgeFormat.appliesTo
        .filter((item) => item.length > 0)
        .map((item, index) => (
          <FdChip key={index} sx={classes.useCaseChip} size="small" label={item} />
        ));
    }
  };

  const showDeleteDialog = () => {
    setConfirmationDialog({
      open: true,
      title: "Are you sure you want to delete this size?",
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      onConfirm: () => {
        dirtyForm && setDirtyForm(false); // we don't want to show the form leave warning when redirecting after deletion
        handleDeleteSize();
      },
      onCancel: handleDialogClose,
    });
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const handleDeleteSize = async () => {
    try {
      await api.badgeFormatService.deleteBadgeFormat(badgeFormat?.id);
      redirect(`/${routePaths.BADGE_SIZES}`);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onSubmit = async (values: any) => {
    if (badgeFormat) {
      updateBadgeFormat(values);
    } else {
      createBadgeFormat(values);
    }

    setDirtyForm(false);
  };

  const createBadgeFormat = async (values: any) => {
    try {
      setLoading(true);
      const newBadgeFormat = await api.badgeFormatService.createBadgeFormat({
        name: values.name,
        width: values.width,
        height: values.height,
        bleed: values.bleed,
        perforation: values.perforation,
        doubleSided: values.isDoubleSided,
        default: values.isDefault,
      });
      setBadgeFormat(newBadgeFormat);
      setLoading(false);
      notificationRef.current?.show(
        "Format successfully created",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const updateBadgeFormat = async (values: any) => {
    try {
      setLoading(true);
      const updatedBadgeFormat = await api.badgeFormatService.updateBadgeFormat(
        badgeFormat?.id,
        {
          name: values.name,
          width: values.width,
          height: values.height,
          bleed: values.bleed,
          perforation: values.perforation,
          doubleSided: values.isDoubleSided,
          isDefault: values.isDefault,
        }
      );
      setBadgeFormat(updatedBadgeFormat);
      setLoading(false);
      notificationRef.current?.show(
        "Format successfully saved",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDuplicateItem = async () => {
    try {
      const duplicatedBadgeFormat =
        await api.badgeFormatService.createBadgeFormat({
          name: `${badgeFormat?.name}-copy`,
          width: badgeFormat?.width,
          height: badgeFormat?.height,
          bleed: badgeFormat?.bleed,
          perforation: badgeFormat?.perforation,
          doubleSided: badgeFormat?.doubleSided,
          default: badgeFormat?.default,
        });
      setBadgeFormat(duplicatedBadgeFormat);
      redirect(
        `/${routePaths.BADGE_SIZES}/${duplicatedBadgeFormat.id}/${routePaths.EDIT}`
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const initialValues = {
    name: badgeFormat?.name,
    width: badgeFormat?.width,
    height: badgeFormat?.height,
    bleed: badgeFormat?.bleed,
    perforation: badgeFormat?.perforation,
    isDefault: badgeFormat?.default,
    isDoubleSided: badgeFormat?.doubleSided,
  };

  const badgeUseCases = getBadgeUseCases();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: initialValues,
  });
  useEffect(() => {
    reset(initialValues)
  },[reset, badgeFormat])

  return (
    <>
      {loading && <Loading />}
      {!loading && (
        <Box sx={classes.wrapper}>
          <Box sx={classes.contentWrapper}>
            <Box sx={classes.topActions}>
              <Box sx={classes.infoTitle}>
                <Box textAlign="left">
                  <FdTypography
                    label={badgeFormat?.name || "New size"}
                    variant="h2"
                    component="h2"
                    sx={{ marginBottom: "10px" }}
                  />
                </Box>
              </Box>

              {badgeFormat && (
                <>
                  <FdButton
                    startIcon={<IoMdCopy />}
                    onClick={() => {
                      dirtyForm && setDirtyForm(false); // we don't want to show the form leave warning when redirecting after duplicate
                      handleDuplicateItem();
                    }}
                    variant="text"
                    ButtonName="Duplicate"
                  />
                  <FdButton
                    sx={{ marginLeft: "15px" }}
                    startIcon={<BiTrash />}
                    onClick={() => {
                      showDeleteDialog();
                    }}
                    variant="text"
                    ButtonName="Delete"
                  />
                </>
              )}

              <FdButton
                variant="contained"
                color="primary"
                size="medium"
                // onClick={(event) => {
                //   submit(event);
                // }}
                type="submit"
                sx={classes.saveButton}
                ButtonName="Save"
                form="reservationForm"
              />
              <FdIconButton
                aria-label="fd-close"
                onClick={() => {
                  redirect(`/${routePaths.BADGE_SIZES}`);
                }}
                size="large"
                // variant="text"
                Icon={<CloseIcon />}
              />
            </Box>

            <Box sx={classes.formContent}>
              {/* <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                validate={validate}
                render={({ handleSubmit, values }) => {
                  submit = handleSubmit;
                  return ( */}
                    <form
                      id="reservationForm"
                      onSubmit={handleSubmit(onSubmit)}
                      noValidate
                      style={{
                        width: "100%",
                        textAlign: "left",
                        height: "100%",
                        display: "contents",
                      }}
                    >
                      <Box sx={classes.genericFields}>
                        <Box sx={classes.formFields}>
                          {/* badge format name */}
                          <Box mb="3px">
                            <FdTextField
                              register={register}
                              name="name"
                              label="Name"
                              required
                              error={errors.name?.type === "required"}
                              errorMsg={errors.name && "Required"}
                            />
                          </Box>

                          {/* badge format width */}
                          <Box mb="3px">
                            <FdTextField
                              register={register}
                              name="width"
                              label="Width"
                              required
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    mm
                                  </InputAdornment>
                                ),
                              }}
                              type="number"
                              sx={classes.inputWithAdornment}
                              error={errors.width?.type === "required"}
                              errorMsg={errors.width &&  "Required"}
                            />
                          </Box>

                          {/* badge format height */}
                          <Box mb="3px">
                            <FdTextField
                              register={register}
                              name="height"
                              label="Height"
                              required
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    mm
                                  </InputAdornment>
                                ),
                              }}
                              type="number"
                              sx={classes.inputWithAdornment}
                              error={errors.height?.type === "required"}
                              errorMsg={errors.height?.type && "Required"}
                            />
                          </Box>

                          {/* badge format bleed */}
                          <Box mb="3px">
                            <FdTextField
                              register={register}
                              name="bleed"
                              label="Bleed"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    mm
                                  </InputAdornment>
                                ),
                              }}
                              type="number"
                              sx={classes.inputWithAdornment}
                              required
                              error={errors.bleed?.type === "required"}
                              errorMsg={errors.bleed?.type && "Required"}
                            />
                          </Box>

                          {/* badge format perforation */}
                          <Box>
                            <FdTextField
                              register={register}
                              name="perforation"
                              label="Perforation"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    mm
                                  </InputAdornment>
                                ),
                              }}
                              type="number"
                              sx={classes.inputWithAdornment}
                              required
                              error={errors.perforation?.type === "required"}
                              errorMsg={errors.perforation?.type && "Required"}
                            />
                          </Box>
                    <Controller
                      name="isDefault"
                      control={control}
                      render={({ field }) => (
                        <FdCheckbox
                          register={register}
                          name="isDefault"
                          // defaultValue={initialValues.isDefault}
                          defaultChecked={initialValues.isDefault}
                          color="primary"
                          onChange={(e) => field.onChange(e.target.checked)}
                          checked={field.value}
                          label="Default"
                          sx={classes.checkbox}
                          control={control}
                        />
                      )}
                    />
                    <Controller
                      name="isDoubleSided"
                      control={control}
                      render={({ field }) => (
                        <FdCheckbox
                          register={register}
                          name="isDoubleSided"
                          defaultValue={initialValues.isDoubleSided}
                          defaultChecked={initialValues.isDoubleSided}
                          color="primary"
                          onChange={(e) => field.onChange(e.target.checked)}
                          checked={field.value}
                          label="Double sided"
                          sx={classes.checkbox}
                          control={control}
                        />
                      )}
                    />

                    {/* <FdCheckbox
                      register={register}
                      name="isDoubleSided"
                      value={false}
                      defaultValue={initialValues.isDoubleSided}
                      // data={{ label: "Double sided", value: false }}
                      sx={classes.checkbox}
                    /> */}
                  </Box>
                </Box>

                      {badgeFormat && (
                        <Box sx={classes.useCases}>
                          <Box
                            sx={{
                              padding: "20px 20px 10px 20px",
                              fontWeight: "bold",
                            }}
                          >
                            Applied to:
                          </Box>
                          <Box padding="20px">{badgeUseCases}</Box>
                        </Box>
                      )}

                      {/* <FormSpy
                        subscription={{ pristine: true }}
                        onChange={(props) => {
                          setTimeout(() => {
                            if (!dirtyForm && !props.pristine)
                              setDirtyForm(!props.pristine);
                          }, 0);
                        }}
                      /> */}

                      {/* this is for showing a warning when leaving the form with unsaved updates */}
                      <RouteLeavingGuard
                        when={isDirty}
                        navigate={(path) => navigate(path)}
                        shouldBlockNavigation={(location) => isDirty}
                      />
                    </form>
                  {/* );
              //   }} */}
              {/* // /> */}
            </Box>
          </Box>
        </Box>
      )}
      <NotificationMessage ref={notificationRef} />

      {confirmationDialog.open && (
        <SimpleActionDialog
          type={confirmationDialog.type}
          icon={confirmationDialog.icon}
          open={confirmationDialog.open}
          title={confirmationDialog.title}
          isLoading={confirmationDialog.isLoading}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
          onClose={handleDialogClose}
          content={confirmationDialog.content}
          confirmLabel={confirmationDialog.confirmLabel}
        />
      )}
    </>
  );
};

export default BadgeSizeForm;
